/**
 * Environment configuration. Per SASATH-0296, tmm-api is reachable
 * same-origin at `/api/*`, rewritten onto tmm-api's real context at the
 * edge — this app derives no API hostname. Every other platform host —
 * login, auth, brand, asset, pay — is a platform singleton anchored to the
 * tenant apex, not to this app's host, and cannot be derived; it is
 * bootstrapped once at app start (`src/config/bootstrap.ts`) and used
 * verbatim. Ported from dink-admin DNKADM-0273 (composed all four the same
 * way and shipped hostnames the platform never mints, routes, or certifies);
 * DNKADM-0274 cut that to a single derived host; DNKADM-0315 removes the last
 * one. There are no URL environment variables or URL defaults. The only build
 * variable is the deploy-environment stamp.
 *
 * `window.location` is the single source for the app origin and the API
 * base. Missing browser location data is a hard error because no safe
 * fallback can be derived.
 */
type DeployEnv = 'staging' | 'production';

export interface AppEnv {
  deployEnv: DeployEnv;
  /** tmm-api base — bearer calls at `${apiBaseUrl}/v1/reseller-admin/...`. Same-origin: this
   *  console's own origin plus the fixed `/api` prefix, rewritten onto tmm-api's real context
   *  at the edge. No hostname is derived. */
  apiBaseUrl: string;
  /** This console's own origin, derived from the browser at runtime. */
  appOrigin: string;
}

/**
 * The same-origin API prefix, rewritten onto tmm-api's real context at the
 * edge (SASATH-0296). A product constant, identical in every environment —
 * never a build variable.
 */
const API_PREFIX = '/api';

/**
 * The browser location. There is NO fallback: without it the api origin
 * cannot be derived, and a placeholder would silently produce a wrong origin
 * (ported from dink-admin DNKADM-0273). Failing here is the intended behavior.
 */
function requireBrowserLocation(): { origin: string } {
  const loc = typeof window !== 'undefined' ? window.location : undefined;
  if (!loc?.origin || !loc.host || !loc.protocol) {
    throw new Error('Cannot derive the API origin: window.location is unavailable.');
  }
  return { origin: loc.origin };
}

function normalizeDeployEnv(value: string | undefined): DeployEnv | null {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'staging' || normalized === 'production') {
    return normalized;
  }
  return null;
}

function hostHasStagingLabel(value: string): boolean {
  try {
    return new URL(value).hostname.split('.').includes('staging');
  } catch {
    return false;
  }
}

function inferDeployEnv(appOrigin: string): DeployEnv {
  if (hostHasStagingLabel(appOrigin)) {
    return 'staging';
  }
  return 'production';
}

const browserLocation = requireBrowserLocation();

/**
 * Same-origin: this console's own origin plus the fixed `/api` prefix. No
 * hostname is derived — `/tmm-api` is an edge concern the browser never
 * sees (SASATH-0296).
 */
const apiBaseUrl = `${browserLocation.origin}${API_PREFIX}`;

const deployEnv =
  normalizeDeployEnv(import.meta.env.VITE_DEPLOY_ENV) ?? inferDeployEnv(browserLocation.origin);

export const env: AppEnv = { deployEnv, apiBaseUrl, appOrigin: browserLocation.origin };
